import React, { useState } from 'react';
import CalcShell from '../CalcShell';

function CalcButtons({ onCalc, onClear, testId }) {
  return (
    <div className="flex gap-2 mt-3">
      <button onClick={onCalc} data-testid={`${testId}-calc`} className="inline-flex items-center gap-2 px-5 py-2 rounded bg-[#4c7b25] hover:bg-[#4c7b25] text-white font-semibold text-sm shadow-sm">
        <span className="inline-block w-0 h-0 border-l-[7px] border-l-white border-y-[5px] border-y-transparent"></span> Calculate
      </button>
      <button onClick={onClear} data-testid={`${testId}-clear`} className="px-5 py-2 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium text-sm">Clear</button>
    </div>
  );
}

function ResultLine({ label, value }) {
  return (
    <div className="mt-3 bg-white rounded-lg p-3 border border-slate-200">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-mono text-lg font-semibold text-blue-700 break-all">{value}</div>
    </div>
  );
}

function BinArithSection() {
  const [a, setA] = useState('1010');
  const [b, setB] = useState('1011');
  const [op, setOp] = useState('+');
  const [result, setResult] = useState(null);
  const clean = s => s.replace(/[^01-]/g, '');
  const calc = () => {
    const aa = clean(a), bb = clean(b);
    const na = parseInt(aa, 2), nb = parseInt(bb, 2);
    if (isNaN(na) || isNaN(nb)) { setResult({ err: 'Invalid binary input (use only 0 and 1).' }); return; }
    let r;
    if (op === '+') r = na + nb;
    else if (op === '−') r = na - nb;
    else if (op === '×') r = na * nb;
    else if (op === '÷') r = nb === 0 ? null : Math.floor(na / nb);
    if (r === null) { setResult({ err: 'Divide by zero' }); return; }
    setResult({
      bin: (r < 0 ? '-' : '') + Math.abs(r).toString(2),
      dec: r,
      hex: (r < 0 ? '-' : '') + Math.abs(r).toString(16).toUpperCase()
    });
  };
  const clear = () => { setA(''); setB(''); setOp('+'); setResult(null); };
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900">Binary Calculation—Add, Subtract, Multiply, or Divide</h3>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2">
          <input value={a} onChange={e => setA(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white font-mono w-40" />
          <select value={op} onChange={e => setOp(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white">
            <option>+</option><option>−</option><option>×</option><option>÷</option>
          </select>
          <input value={b} onChange={e => setB(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white font-mono w-40" />
          <span className="text-slate-500 font-mono">= ?</span>
        </div>
        <CalcButtons testId="bin-arith" onCalc={calc} onClear={clear} />
        {result?.err && <div className="mt-3 text-red-600 text-sm">{result.err}</div>}
        {result && !result.err && (
          <>
            <ResultLine label="Binary" value={result.bin} />
            <ResultLine label="Decimal" value={result.dec} />
            <ResultLine label="Hexadecimal" value={result.hex} />
          </>
        )}
      </div>
    </div>
  );
}

function BinToDecSection() {
  const [bin, setBin] = useState('10101010');
  const [result, setResult] = useState(null);
  const calc = () => {
    const n = parseInt(bin.trim(), 2);
    if (isNaN(n)) { setResult({ err: 'Invalid binary value' }); return; }
    setResult({ dec: n, hex: n.toString(16).toUpperCase(), oct: n.toString(8) });
  };
  const clear = () => { setBin(''); setResult(null); };
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Convert Binary Value to Decimal Value</h3>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-700">Binary Value:</span>
          <input value={bin} onChange={e => setBin(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white font-mono w-52" />
          <span className="text-slate-500 font-mono">= ?</span>
        </div>
        <CalcButtons testId="bin-to-dec" onCalc={calc} onClear={clear} />
        {result?.err && <div className="mt-3 text-red-600 text-sm">{result.err}</div>}
        {result && !result.err && (
          <>
            <ResultLine label="Decimal" value={result.dec} />
            <ResultLine label="Hexadecimal" value={result.hex} />
            <ResultLine label="Octal" value={result.oct} />
          </>
        )}
      </div>
    </div>
  );
}

function DecToBinSection() {
  const [dec, setDec] = useState(170);
  const [result, setResult] = useState(null);
  const calc = () => {
    const n = Number(dec);
    if (!Number.isFinite(n)) { setResult({ err: 'Invalid decimal value' }); return; }
    const i = Math.floor(Math.abs(n));
    const sign = n < 0 ? '-' : '';
    setResult({ bin: sign + i.toString(2), hex: sign + i.toString(16).toUpperCase(), oct: sign + i.toString(8) });
  };
  const clear = () => { setDec(0); setResult(null); };
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Convert Decimal Value to Binary Value</h3>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-700">Decimal Value:</span>
          <input type="number" value={dec} onChange={e => setDec(e.target.value)} className="px-3 py-2 rounded border border-slate-300 bg-white font-mono w-40" />
          <span className="text-slate-500 font-mono">= ?</span>
        </div>
        <CalcButtons testId="dec-to-bin" onCalc={calc} onClear={clear} />
        {result?.err && <div className="mt-3 text-red-600 text-sm">{result.err}</div>}
        {result && !result.err && (
          <>
            <ResultLine label="Binary" value={result.bin} />
            <ResultLine label="Hexadecimal" value={result.hex} />
            <ResultLine label="Octal" value={result.oct} />
          </>
        )}
      </div>
    </div>
  );
}

export default function BinaryCalc() {
  return (
    <CalcShell look="cnet" title="Binary Calculator" subtitle="Modify the values and click the Calculate button to use">
      <BinArithSection />
      <BinToDecSection />
      <DecToBinSection />
    </CalcShell>
  );
}
